import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useLayoutEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import styled, {css, keyframes} from 'styled-components';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import type {ControlState, DisabledReason} from './controlState';
import {anchorElementId, novaInstanceKey, controlElementId, useIsPickerMounted, useIsPickerOpen} from './NovaAgentPicker';
import {formatCreditsReason} from './novaCreditsCopy';
import {RUN_CAP} from './novaInvocationStore';

export interface InvocationControlProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx) — must match whatever the
	// sibling agentPicker below was given, or the caret's id and the picker's toggle/lookup key
	// disagree and the picker can't find its own caret.
	instanceKey?: string;
	// The instance whose agent list the caret should open once this task already has a conversation.
	// That list is the conversation's own, which confirms before switching agent - a switch there
	// discards the thread, and this control's own list invokes straight away because it is only ever
	// offered before a run exists. Absent, the caret opens this control's own list, as it always has.
	conversationPickerInstanceKey?: string;
	// Where this control is mounted. 'row' is the quiet chip a task list shows among other chips;
	// 'header' is the filled primary the task modal gives it, where starting a run is the one thing
	// the header offers. Only the treatment differs - every state, label and refusal below is the
	// same control either way, because a person meets both and they must not disagree.
	variant?: 'row' | 'header';
	state: ControlState;
	// The day a spent AI allowance resets, for the out_of_credits reason.
	creditsResetsOn?: string | null;
	onAction: () => void;
	onOpenOutput: () => void;
	onStop: () => void;
	agentPicker?: React.ReactNode; // rendered inline below the control when the picker (a later task) is mounted; undefined until then
}

// Every reason says a different thing, and each names a cause the person can tell apart from the
// others: the task is locked, you may not edit tasks, Nova is busy with three already, there is no
// agent to run. None of them explains what to do instead — the disabled control and its reason are
// the whole of what this surface says.
const REASON_MESSAGE_ID: Record<DisabledReason, string> = {
	task_locked: 'nova_invocation.control.reason_task_locked',
	no_edit_task: 'nova_invocation.control.reason_no_edit_task',
	run_cap_reached: 'nova_invocation.control.reason_run_cap',
	no_agent_available: 'nova_invocation.control.reason_no_agent_available',
	out_of_credits: 'nova_invocation.control.reason_out_of_credits',
};

let disabledReasonIdCounter = 0;

function useDisabledReasonId(): string {
	const idRef = useRef<string>();
	if (!idRef.current) {
		disabledReasonIdCounter += 1;
		idRef.current = `nova-disabled-reason-${disabledReasonIdCounter}`;
	}
	return idRef.current;
}

// This control is a small tinted status pill (e.g. light-blue "Action", light-green pulsing
// "Ready"), not a standard filled/outlined button — no design-system Button emphasis produces
// that look, so it's built here from design tokens rather than composed from Button.
type ChipTone = 'blue' | 'green' | 'neutral';

// Optional chaining because this module loads transitively through the shared web-components
// barrel — unrelated test files that mock '@forecast-it/design-system' without providing
// tokensForThemeProvider would otherwise crash on import alone, before any test even runs.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

const CHIP_TONE_COLORS: Record<ChipTone, {background: string; border: string; text: string}> = {
	blue: {background: ref?.blue['50'] ?? '', border: ref?.blue['100'] ?? '', text: ref?.blue['900'] ?? ''},
	green: {background: ref?.green['50'] ?? '', border: ref?.green['75'] ?? '', text: sys?.text.semantic.success ?? ''},
	neutral: {
		background: ref?.neutral['200'] ?? '',
		border: ref?.neutral['300'] ?? '',
		text: ref?.neutral['400'] ?? '',
	},
};

const spin = keyframes`
	to { transform: rotate(360deg); }
`;

// Deliberately still, in every state. An unread answer announces itself by its own colour and its
// own word - a green chip that says Ready where a blue one said Action - and that is a thing the
// reader finds when they look. A chip that pulses instead keeps asking, from the corner of the eye,
// for as long as it goes unread, on every row of a list at once.
const chipStyles = css<{$tone: ChipTone}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 999px;
	font-family: inherit;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	padding: 5px 11px;
	border: 1px solid ${({$tone}) => CHIP_TONE_COLORS[$tone].border};
	background: ${({$tone}) => CHIP_TONE_COLORS[$tone].background};
	color: ${({$tone}) => CHIP_TONE_COLORS[$tone].text};

	/* The mark takes the chip's own colour rather than naming one, so it reads correctly on every
	   tone the chip has - blue while there is something to start, green while an answer waits. */
	svg path {
		fill: currentColor;
	}
`;

// The task modal's own treatment of the same control. On a row the chip sits among other chips and
// has to stay quiet; in the modal header it is the one thing that starts a run, so the design gives
// it the filled primary shape every other header action defers to, with the agent caret joined onto
// its right edge rather than floating beside it.
const HEADER_SURFACE = ref?.blue['900'] ?? '';
const HEADER_TEXT = sys?.text.content.ondark ?? '';
const HEADER_DISABLED_SURFACE = ref?.neutral['200'] ?? '';
const HEADER_DISABLED_TEXT = ref?.neutral['400'] ?? '';
const HEADER_RADIUS = size?.borderradius['3'] ?? '';
// The seam between the two halves, drawn on the filled surface itself rather than as a border
// colour, so it reads as one control divided rather than two buttons touching.
const HEADER_DIVIDER = 'rgba(255, 255, 255, 0.28)';

const headerChipStyles = css<{$working?: boolean}>`
	gap: 7px;
	padding: 8px 14px;
	border: none;
	border-radius: ${HEADER_RADIUS} 0 0 ${HEADER_RADIUS};
	background: ${HEADER_SURFACE};
	color: ${HEADER_TEXT};
	/* A working chip is disabled - there is nothing to press while a run is going - but it is not
	   refused, and the muted pair below says refused. It keeps the filled surface so the control
	   reads as the same thing it was a moment ago, now reporting on itself. */
	&:disabled {
		background: ${({$working}) => ($working ? HEADER_SURFACE : HEADER_DISABLED_SURFACE)};
		color: ${({$working}) => ($working ? HEADER_TEXT : HEADER_DISABLED_TEXT)};
	}
`;

const ChipButton = styled.button<{$tone: ChipTone; $header?: boolean; $working?: boolean}>`
	${chipStyles}
	cursor: pointer;
	&:disabled {
		cursor: not-allowed;
	}
	${({$header}) => $header && headerChipStyles}
`;

// The product's AI mark, on every chip that offers a run. aria-hidden throughout: the chip's own
// label already says what pressing it does, and a second announcement of "sparkle" beside it is
// noise to a screen reader.
const NovaMark = () => <Icon icon="sparkle" size="s" aria-hidden="true" />;

// Reuses CHIP_TONE_COLORS.blue rather than reading ref.blue[...] directly — ref itself can be
// undefined (see the optional chaining above), and CHIP_TONE_COLORS already guards that.
const Spinner = styled.span<{$header?: boolean}>`
	display: inline-block;
	flex: none;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	/* Drawn from the chip's own text colour rather than a fixed blue: on the row it turns on a pale
	   blue chip, in the header on a filled blue one, where a blue spinner is invisible. */
	border: 2px solid ${({$header}) => ($header ? 'rgba(255, 255, 255, 0.35)' : CHIP_TONE_COLORS.blue.border)};
	border-top-color: ${({$header}) => ($header ? HEADER_TEXT : CHIP_TONE_COLORS.blue.text)};
	animation: ${spin} 0.7s linear infinite;
`;

const CaretButton = styled.button<{$header?: boolean}>`
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border-radius: 999px;
	border: 1px solid ${CHIP_TONE_COLORS.blue.border};
	background: ${CHIP_TONE_COLORS.blue.background};
	color: ${CHIP_TONE_COLORS.blue.text};
	font-size: 10px;
	line-height: 14px;
	cursor: pointer;
	/* Joined onto the chip's right edge rather than circling beside it: in the header the two read
	   as one control with a second half, which is what makes the caret's relationship to the run it
	   configures legible without a label. */
	${({$header}) =>
		$header &&
		css`
			width: 28px;
			height: auto;
			align-self: stretch;
			border: none;
			border-left: 1px solid ${HEADER_DIVIDER};
			border-radius: 0 ${HEADER_RADIUS} ${HEADER_RADIUS} 0;
			background: ${HEADER_SURFACE};
			color: ${HEADER_TEXT};
			/* The chevron is a design-system Icon, which paints itself from its own token rather than
			   inheriting - the same reason the chip states this for its mark. Without it the caret
			   draws dark on the filled blue half. */
			svg path {
				fill: currentColor;
			}
			/* Half a step back from the half it configures: pressing the chip starts a run, pressing
			   this only chooses which agent would. Dropped on the disabled pair, where the whole
			   control is already muted and a second mute would read as a third state. */
			opacity: 0.88;
			/* Pointing down at rest, at the picker it drops open. The mockup reaches the same angle
			   from a right-facing path turned 90deg; the design system's chevronDown already faces
			   that way, so rotating it at rest would turn it past where the mockup leaves it. The
			   half-turn it takes while the list is open is on the glyph itself - see CaretGlyph. */
			&:disabled {
				background: ${HEADER_DISABLED_SURFACE};
				color: ${HEADER_DISABLED_TEXT};
				opacity: 1;
				cursor: not-allowed;
			}
		`}
`;

// The chevron inside the caret. Turned a half-turn while the list it opens is on screen and back
// when that closes, so the control states which way the list went rather than leaving the reader to
// remember. Separate from the button because the button is the thing that stays put: turning it
// would take its border and its filled half round with the glyph.
const CaretGlyph = styled.span<{$open: boolean}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: transform 0.15s;
	transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
`;

// Same circular shape as CaretButton — occupies the same slot the caret would, since the two
// never show at once (the caret disappears once a run starts, see showCaret below).
const StopButton = styled.button<{$header?: boolean}>`
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border-radius: 999px;
	border: 1px solid ${CHIP_TONE_COLORS.blue.border};
	background: ${CHIP_TONE_COLORS.blue.background};
	cursor: pointer;
	/* Joined onto the working chip's right edge, exactly where the caret sits on the others: while a
	   run is going this is the control's second half, and a pale circle floating beside a filled
	   chip reads as a different control that happens to be next to it. */
	${({$header}) =>
		$header &&
		css`
			width: 28px;
			height: auto;
			align-self: stretch;
			border: none;
			border-left: 1px solid ${HEADER_DIVIDER};
			border-radius: 0 ${HEADER_RADIUS} ${HEADER_RADIUS} 0;
			background: ${HEADER_SURFACE};
		`}
`;

const StopIcon = styled.span<{$header?: boolean}>`
	display: inline-block;
	flex: none;
	width: 7px;
	height: 7px;
	border-radius: 1px;
	background: ${({$header}) => ($header ? HEADER_TEXT : CHIP_TONE_COLORS.blue.text)};
`;

const ChipRow = styled.span<{$header?: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	/* Joined, and the same height. On a row the caret is its own round button sitting beside the
	   chip; in the header the two are one control split in two, so the gap goes and the caret takes
	   the chip's height rather than keeping its own. */
	${({$header}) =>
		$header &&
		css`
			align-items: stretch;
			gap: 0;
		`}
`;

// This region exists only so a screen reader announces the working transition (see the focus
// effect below) — it must never be visible. Standard "visually hidden" clip, not display:none,
// so it stays in the accessibility tree while taking no visual space.
const StatusRegion = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

// Every state's own native `title` (see each ChipButton below) is the row's one explanatory
// surface — the mockup's row-level Nova pill has no separate always-visible banner text in any
// state, disabled or not: a full run-cap reads exactly like any other disabled reason (grayed
// pill, reason on hover), and a near-cap warning has no row treatment at all in the design — that
// banner belongs to the task-detail composer instead (no host for it in this codebase yet, same
// position as FailurePresentation.tsx).
export const NovaInvocationControl = (props: InvocationControlProps): JSX.Element => {
	const {state} = props;
	const intl = useIntl();
	const {formatMessage} = intl;
	const isHeader = props.variant === 'header';
	const disabledReasonId = useDisabledReasonId();
	const stopButtonRef = useRef<HTMLButtonElement>(null);

	// The Working pill renders disabled (see the 'working' case below) — a browser can't keep focus
	// on a button the instant it becomes disabled, so a keyboard user who just pressed Action would
	// otherwise lose focus to the document body the moment the row flips to Working. Moving it onto
	// the new stop button (see StopButton below) keeps a keyboard user's focus somewhere useful.
	// previousKindRef excludes the initial mount (a row that starts out already Working, e.g. on
	// page load) — only an actual action→working transition should steal focus.
	const previousKindRef = useRef<ControlState['kind'] | null>(null);
	// A favourited task on My Work mounts two instances of this control for the same taskId
	// (Draggable_card's card and Task_row's row — see each one's own instanceKey comment). Both
	// subscribe to the same store entry and both observe the same action→working transition, so
	// without this guard, clicking Action on one instance moves focus onto whichever instance's
	// effect happens to run last (React runs sibling effects in mount order), not necessarily the
	// one actually clicked. Set synchronously in the click handler below, read and cleared here —
	// only the instance whose own button raised this transition ever moves focus.
	const actionClickedHereRef = useRef(false);
	useLayoutEffect(() => {
		if (
			previousKindRef.current !== null &&
			previousKindRef.current !== 'working' &&
			state.kind === 'working' &&
			actionClickedHereRef.current
		) {
			stopButtonRef.current?.focus();
		}
		actionClickedHereRef.current = false;
		previousKindRef.current = state.kind;
	}, [state.kind]);

	// Whether the list this control's caret is aimed at is actually on screen. See showCaret in the
	// ready_or_continue branch below for what rides on it.
	const conversationPickerMounted = useIsPickerMounted(props.taskId, props.conversationPickerInstanceKey);

	// The list this caret opens, which on a task that already has a conversation is that
	// conversation's own rather than this control's. Asked of the same key the caret toggles, so the
	// glyph turns for the list that actually opened.
	const pickerOpen = useIsPickerOpen(
		props.taskId,
		state.kind === 'ready_or_continue' && props.conversationPickerInstanceKey
			? props.conversationPickerInstanceKey
			: props.instanceKey
	);

	let button: React.ReactNode;
	let statusText = '';
	let disabledReasonSpan: React.ReactNode = null;
	let showCaret = false;
	let showStop = false;

	switch (state.kind) {
		case 'disabled': {
			// Also the button's native title — a disabled reason is meant to be legible to every
			// user on hover, not just announced to a screen reader (see the mockup's row tooltip
			// on the capped/locked Action button). RUN_CAP is passed for every reason, not just
			// run_cap_reached - react-intl ignores an interpolation value a message doesn't use, and
			// this keeps the run-cap message honest if RUN_CAP ever changes, instead of a hardcoded
			// "3" silently going stale in five locales.
			const reasonText =
				state.reason === 'out_of_credits'
					? formatCreditsReason(intl, props.creditsResetsOn)
					: formatMessage({id: REASON_MESSAGE_ID[state.reason]}, {count: RUN_CAP});
			button = (
				<ChipButton
					$header={isHeader}
					$tone="neutral"
					disabled
					title={reasonText}
					aria-describedby={disabledReasonId}
					data-cy="nova-invocation-control"
				>
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
			// Visually hidden, same as StatusRegion below — aria-describedby (on the button above)
			// reads this for a screen reader regardless of visibility. The title above already
			// covers sighted users; this only needs to reach assistive tech.
			disabledReasonSpan = <StatusRegion id={disabledReasonId}>{reasonText}</StatusRegion>;
			break;
		}
		case 'working':
			button = (
				<ChipButton $tone="blue" $header={isHeader} $working disabled data-cy="nova-invocation-control">
					<Spinner $header={isHeader} />
					{formatMessage({id: 'nova_invocation.control.working'})}
				</ChipButton>
			);
			statusText = formatMessage({id: 'nova_invocation.control.status_working'});
			showStop = true;
			break;
		case 'ready_or_continue':
			// The caret belongs to the modal header's control, where the design pairs it with Continue
			// so a conversation can be restarted on another agent from the same place it was begun.
			// Withheld on an unread answer: nothing has been read yet, so there is no decision to
			// revisit, and withheld on the row and card surfaces, whose caret is for starting a run.
			//
			// Also withheld while the conversation's own list is not mounted - a thread whose turns
			// never arrived because the run service was unreachable, or one on a task this person may
			// not act on, which mounts no list precisely so that no caret is offered. The caret cannot
			// fall back to this control's own picker in that case: that one invokes the chosen agent
			// directly, with no warning that doing so discards the conversation, which is the whole
			// reason the caret is routed away from it here. A control that cannot offer the safe list
			// offers none, rather than a button that silently does nothing or a shortcut past the
			// warning.
			showCaret = isHeader && state.read && (!props.conversationPickerInstanceKey || conversationPickerMounted);
			button = (
				<ChipButton
					$header={isHeader}
					$tone={state.read ? 'blue' : 'green'}
					onClick={props.onOpenOutput}
					title={formatMessage({
						id: state.read ? 'nova_invocation.control.tooltip_continue' : 'nova_invocation.control.tooltip_ready',
					})}
					data-cy="nova-invocation-control"
				>
					<NovaMark />
					{formatMessage({id: state.read ? 'nova_invocation.control.continue' : 'nova_invocation.control.ready'})}
				</ChipButton>
			);
			break;
		case 'action':
			button = (
				<ChipButton
					$header={isHeader}
					$tone="blue"
					onClick={() => {
						actionClickedHereRef.current = true;
						props.onAction();
					}}
					title={formatMessage({id: 'nova_invocation.control.tooltip_action'})}
					data-cy="nova-invocation-control"
				>
					<NovaMark />
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
			showCaret = true;
			break;
		default: {
			const _exhaustive: never = state;
			void _exhaustive;
			button = (
				<ChipButton $tone="neutral" $header={isHeader} disabled data-cy="nova-invocation-control">
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
		}
	}

	return (
		// position: relative so `agentPicker` (a later task's popover) has a defined anchor to
		// position:absolute against, regardless of whatever container each mounting surface
		// happens to wrap this control in.
		<span
			id={controlElementId(props.taskId, props.instanceKey)}
			style={{position: 'relative', display: 'inline-flex', alignItems: 'center'}}
		>
			<ChipRow $header={isHeader}>
				{button}
				{showCaret && (
					<CaretButton
						$header={isHeader}
						aria-label={formatMessage({id: 'nova_invocation.control.choose_agent'})}
						onClick={() =>
							dispatch(
								EVENT_ID.NOVA_TOGGLE_PICKER,
								novaInstanceKey(
									props.taskId,
									// A task with a conversation hands the caret to that conversation's own
									// list, which warns before discarding it. See the prop's own note.
									state.kind === 'ready_or_continue' && props.conversationPickerInstanceKey
										? props.conversationPickerInstanceKey
										: props.instanceKey
								)
							)
						}
						data-cy="nova-invocation-caret"
						id={anchorElementId(props.taskId, props.instanceKey)}
					>
						{/* A drawn chevron in the header, where the caret is a full-height half of a filled
						    control and the text glyph sits too high in it to read as centred. The row keeps
						    the glyph it shipped with. */}
						<CaretGlyph $open={pickerOpen} aria-hidden="true">
							{isHeader ? <Icon icon="chevronDown" size="s" aria-hidden="true" /> : '▾'}
						</CaretGlyph>
					</CaretButton>
				)}
				{showStop && (
					<StopButton
						ref={stopButtonRef}
						$header={isHeader}
						onClick={props.onStop}
						title={formatMessage({id: 'nova_invocation.control.stop'})}
						aria-label={formatMessage({id: 'nova_invocation.control.stop'})}
						data-cy="nova-invocation-stop"
					>
						<StopIcon $header={isHeader} />
					</StopButton>
				)}
			</ChipRow>
			<StatusRegion role="status">{statusText}</StatusRegion>
			{disabledReasonSpan}
			{state.kind === 'action' && props.agentPicker}
		</span>
	);
};
